/* eslint-disable class-methods-use-this */
import { SubscriptionCredentials } from '@nx-smartmonkey/shared/domain';
import { Requests, splitArrayByParts } from '@nx-smartmonkey/shared/helpers';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';

import { Project } from '../../domain/project/Project';
import { SearchPredicate } from '../../domain/searchPredicates/SearchPredicate';
import { Stop } from '../../domain/stop/Stop';
import { StopDTO } from '../../domain/stop/StopDTO';
import { StopFlat } from '../../domain/stop/StopFlat';
import { StopFlatDTO } from '../../domain/stop/StopFlatDTO';
import { Task } from '../../domain/task/Task';
import { TaskDTO } from '../../domain/task/TaskDTO';
import { StopFlatMapper } from '../../mappers/stop/StopFlatMapper';
import { StopMapper } from '../../mappers/stop/StopMapper';
import { TaskMapper } from '../../mappers/task/TaskMapper';
import { SessionRepository } from '../session/SessionRepository';
import { CreatePickupData, StopRepository } from './StopRepository';

export class StopRepositoryApi implements StopRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}

  async fetchStop({ stopId, project }: { stopId: string; project: Project }): Promise<Stop> {
    const response = await this.requests.get({
      uri: `/v2/stop/${stopId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as StopDTO;
    return StopMapper.toDomain(data, project.custom_fields!);
  }

  async fetchSomeStops({ stopIds, project }: { stopIds: string[]; project: Project }): Promise<Array<Stop | null>> {
    const stopsIdsChunks = splitArrayByParts({ inputArray: stopIds, itemsPerChunk: 500 }) as string[];

    const requests = stopsIdsChunks.map((stopIdsChunk) => {
      return {
        method: this.requests.post.bind(this.requests),
        params: {
          uri: `/v2/retrieveStopsById`,
          data: {
            stop_ids: stopIdsChunk,
          },
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const stops = response.flat(1) as StopDTO[];

    return stops.map((stop: StopDTO | null) => {
      return stop !== null ? StopMapper.toDomain(stop, project.custom_fields!) : null;
    });
  }

  async fetchDeliveryByPickup({ pickupId, project }: { pickupId: string; project: Project }): Promise<Stop> {
    const response = await this.requests.get({
      uri: `/v2/stop/${pickupId}/delivery`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as StopDTO;
    return StopMapper.toDomain(data, project.custom_fields!);
  }

  async createStops({
    stops,
    planId,
    project,
    subscriptionCredentials,
  }: {
    stops: Array<Stop>;
    planId?: string;
    project: Project;
    subscriptionCredentials: SubscriptionCredentials;
  }): Promise<Array<Stop>> {
    const plan = planId ? `?plan_id=${planId}` : ``;
    const projectId = project.id ? `${plan !== `` ? `&` : `?`}project_id=${project.id}` : ``;
    const response = await this.requests.post({
      uri: `/v2/stops${plan}${projectId}`,
      data: stops.map((stop) => ({
        ...StopMapper.toRepository(stop, subscriptionCredentials),
        ...(stop.pickup ? { pickup: StopMapper.toRepository(stop.pickup, subscriptionCredentials) } : {}),
      })),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as StopDTO[];
    return data.map((stop) => StopMapper.toDomain(stop, project.custom_fields!));
  }

  async search({
    project,
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates = [],
  }: {
    project: Project;
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<Stop>> {
    const response = await this.requests.post({
      uri: `/v2/stops/search?project_id=${project.id}`,
      data: {
        offset,
        limit,
        predicates: predicates!.filter((p) => p.isValid()).map((p) => p.toRepository()),
        ...(sort_by !== undefined ? { sort_by } : {}),
        ...(sort_direction !== undefined ? { sort_direction } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as PaginatedResult<StopDTO>;

    return {
      ...data,
      docs: data.docs.map((stop) => StopMapper.toDomain(stop, project.custom_fields!)),
    };
  }

  async fetchFlatStops({
    projectId,
    predicates = [],
  }: {
    projectId: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<Array<StopFlat>> {
    const response = await this.requests.post({
      uri: `/v2/stops/flat?project_id=${projectId}`,
      data: {
        predicates: predicates!.filter((p) => p.isValid()).map((p) => p.toRepository()),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as Array<StopFlatDTO>;

    return data.map(StopFlatMapper.toDomain);
  }

  async save({
    stop,
    project,
    subscriptionCredentials,
  }: {
    stop: Stop;
    project: Project;
    subscriptionCredentials: SubscriptionCredentials;
  }): Promise<Stop> {
    const { tasks, ...stopRepository } = StopMapper.toRepository(stop, subscriptionCredentials);

    const response = await this.requests.put({
      uri: `/v2/stop/${stop.id}`,
      data: stopRepository,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as StopDTO;

    return StopMapper.toDomain(data, project.custom_fields!);
  }

  async saveMany({
    stops,
    project,
    subscriptionCredentials,
  }: {
    stops: Stop[];
    project: Project;
    subscriptionCredentials: SubscriptionCredentials;
  }): Promise<Stop[]> {
    const requests = stops.map((stop) => {
      return {
        method: this.requests.put.bind(this.requests),
        params: {
          uri: `/v2/stop/${stop.id}`,
          data: StopMapper.toRepository(stop, subscriptionCredentials),
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const updatedStops = response.flat(1) as StopDTO[];

    return updatedStops.map((stop: StopDTO) => {
      return StopMapper.toDomain(stop, project.custom_fields!);
    });
  }

  async createStopTask({ stop, project }: { stop: Stop; project: Project }): Promise<Task> {
    const response = await this.requests.post({
      uri: `/v2/task?stop_id=${stop.id}`,
      data: {},
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as TaskDTO;

    return TaskMapper.toDomain(data, [
      ...project.custom_fields!.service_report_completed,
      ...project.custom_fields!.service_report_canceled,
      ...project.custom_fields!.service_report_task_completed,
      ...project.custom_fields!.service_report_task_canceled,
    ]);
  }

  async updateStopTask({ task, project }: { task: Task; project: Project }): Promise<Task> {
    const response = await this.requests.put({
      uri: `/v2/task/${task.id}?stop_id=${task.stop_id}`,
      data: TaskMapper.toRepository(task),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as TaskDTO;

    return TaskMapper.toDomain(data, [
      ...project.custom_fields!.service_report_completed,
      ...project.custom_fields!.service_report_canceled,
      ...project.custom_fields!.service_report_task_completed,
      ...project.custom_fields!.service_report_task_canceled,
    ]);
  }

  async deleteStopTask({ task }: { task: Task }): Promise<void> {
    await this.requests.delete({
      uri: `/v2/task/${task.id}?stop_id=${task.stop_id}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async createStopPickup({
    stop,
    pickupData,
    project,
  }: {
    stop: Stop;
    pickupData: CreatePickupData;
    project: Project;
  }): Promise<Stop> {
    const response = await this.requests.put({
      uri: `/v2/stop/${stop.id}`,
      data: {
        pickup: pickupData,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as StopDTO;

    return StopMapper.toDomain(data, project.custom_fields!);
  }

  async deleteStopPickup({ stop }: { stop: Stop }): Promise<void> {
    await this.requests.put({
      uri: `/v2/stop/${stop.id}`,
      data: {
        pickup: null,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async unassign({ stops, project }: { stops: Stop[]; project: Project }): Promise<Stop[]> {
    const response = await this.requests.post({
      uri: `/v2/stops/unassign`,
      data: {
        stop_ids: stops.map((stop) => stop.id),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as StopDTO[];

    return data.map((stop) => StopMapper.toDomain(stop, project.custom_fields!));
  }

  async moveStopsToPlan({
    stopIds,
    planId,
    project,
  }: {
    stopIds: string[];
    planId?: string;
    project: Project;
  }): Promise<Stop[]> {
    const response = await this.requests.post({
      uri: `/v2/stop/move${planId ? `?plan_id=${planId}` : ``}`,
      data: {
        stop_ids: stopIds,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as StopDTO[];

    return data.map((stop) => StopMapper.toDomain(stop, project.custom_fields!));
  }

  async updateConstraints(stopIds: string[], constraints: Record<string, any>): Promise<void> {
    await this.requests.put({
      uri: `/v2/stops/constraints`,
      data: {
        stop_ids: stopIds,
        constraints,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async updateSupervisors(stopIds: string[], supervisors: Array<string>): Promise<void> {
    const requests = stopIds.map((stopId) => {
      return {
        method: this.requests.put.bind(this.requests),
        params: {
          uri: `/v2/stop/${stopId}`,
          data: {
            supervisors,
          },
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    await this.requests.multipleAsyncWait({
      requests,
    });
  }

  async deleteStops(stopIds: string[], project: Project): Promise<Stop[]> {
    const response = await this.requests.put({
      uri: `/v2/stops/delete`,
      data: {
        stop_ids: stopIds,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as StopDTO[];

    return data.map((stop) => StopMapper.toDomain(stop, project.custom_fields!));
  }
}
